/**
 * ./src/modules/common/components/Checkbox/index.tsx
 *
 * Checkbox component is used to display a checkbox with a label.
 *
 * @copyright DesignShop, 2024
 * @since 05/13/2024
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { cn } from '../../lib/utils';
import { Check } from '../icons';

interface CheckboxProps {
  label?: string;
  isChecked?: boolean;
  disabled?: boolean;
  onCheckChange?: (isChecked: boolean) => void;
  type?: 'checkbox' | 'radio';
  size?: 'small' | 'medium';
  className?: string;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    { label, isChecked, onCheckChange, type = 'checkbox', size = 'small', className, ...rest },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [internalChecked, setInternalChecked] = useState<boolean>(!!isChecked);

    // Track if we're in controlled mode (isChecked is defined)
    const isControlled = isChecked !== undefined;

    // Sync internal state with prop when it changes (only in controlled mode)
    useEffect(() => {
      if (isControlled) {
        setInternalChecked(isChecked);
      }
    }, [isChecked, isControlled]);

    const handleChange = () => {
      const newCheckedState = !internalChecked;

      // Always update internal state for uncontrolled mode
      if (!isControlled) {
        setInternalChecked(newCheckedState);
      }

      // Always call the change handler if provided
      onCheckChange?.(newCheckedState);
    };

    useImperativeHandle(ref, () => inputRef.current!);

    // Determine which checked state to use
    const checked = isControlled ? isChecked : internalChecked;

    return (
      <div className={`flex w-fit items-center ${className && className}`} onClick={handleChange}>
        <div className="relative flex items-center justify-center">
          <input
            type="checkbox"
            checked={checked}
            onChange={handleChange}
            ref={inputRef}
            id={label}
            className={cn(
              `cursor-pointer appearance-none border bg-gray-200 checked:border-transparent checked:bg-black ${type === 'checkbox' ? 'rounded' : 'rounded-full'} disabled:opacity-50`,
              {
                'h-5 w-5': size === 'small',
                'h-[22px] w-[22px]': size === 'medium',
              }
            )}
            {...rest}
          />
          {checked && type === 'checkbox' && (
            <Check className="pointer-events-none absolute text-white" />
          )}
          {checked && type === 'radio' && (
            <span className="pointer-events-none absolute flex h-2 w-2 rounded-full bg-white" />
          )}
        </div>
        {label && (
          <label className="ml-2" htmlFor={label}>
            {label}
          </label>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
export default Checkbox;
